import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { deleteUserGoals, deleteUserReflections, deleteUserDoc } from '../firebase/utils';
import { updateDoc, doc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db } from '../firebase/config';

const PrivacyModal = ({ isOpen, onClose }) => (
  <Transition appear show={isOpen} as={Fragment}>
    <Dialog as="div" className="relative z-[100]" onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/25" />
      </Transition.Child>

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex justify-between items-start">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Privacy Information
              </Dialog.Title>
              <button
                onClick={onClose}
                className="rounded-full p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3 text-sm text-gray-600">
              <p>We take your privacy seriously. Here's what you should know:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Your data is stored securely in Firebase.</li>
                <li>We only collect information that's necessary for the app's functionality.</li>
                <li>Your goals and reflections are private and only visible to you.</li>
                <li>You can request to delete your account and data at any time.</li>
                <li>Notification preferences are stored locally and can be changed anytime.</li>
              </ul>
              <p className="text-xs text-gray-500 mt-4">
                For more information, please contact our support team.
              </p>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
);

const DeleteAccountModal = ({ isOpen, onClose, onConfirm }) => (
  <Transition appear show={isOpen} as={Fragment}>
    <Dialog as="div" className="relative z-[100]" onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/25" />
      </Transition.Child>

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-3 text-red-600">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <Dialog.Title className="text-lg font-medium">
                Delete Account
              </Dialog.Title>
            </div>
            <div className="space-y-3 text-sm text-gray-600">
              <p className="font-medium text-gray-900">This action cannot be undone.</p>
              <p>
                Deleting your account will:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Permanently remove all your goals</li>
                <li>Delete all your reflections and journal entries</li>
                <li>Remove all your preferences and settings</li>
                <li>Cancel any active notifications</li>
              </ul>
              <p className="text-sm text-red-600 font-medium mt-4">
                Are you sure you want to delete your account?
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Yes, Delete My Account
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
);

const ProfileEditModal = ({ 
  isOpen, 
  onClose,
  editedName,
  setEditedName,
  selectedAvatar,
  setSelectedAvatar,
  onSave
}) => (
  <Transition appear show={isOpen} as={Fragment}>
    <Dialog as="div" className="relative z-[100]" onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/25" />
      </Transition.Child>

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="bg-white rounded-2xl p-6 max-w-md w-full space-y-6">
            <div className="flex justify-between items-start">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Edit Profile
              </Dialog.Title>
              <button
                onClick={onClose}
                className="rounded-full p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Avatar Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Avatar
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {[...Array(8)].map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedAvatar(index)}
                      className={`relative rounded-lg overflow-hidden aspect-square ${
                        selectedAvatar === index ? 'ring-2 ring-primary-500' : 'hover:ring-2 hover:ring-gray-300'
                      }`}
                    >
                      <img
                        src={`https://api.dicebear.com/7.x/adventurer-neutral/svg?seed=${index}&mouth=variant01,variant27`}
                        alt={`Avatar option ${index + 1}`}
                        className="w-full h-full"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Name Input */}
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Display Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your name"
                />
              </div>

              {/* Save Button */}
              <button
                onClick={onSave}
                className="w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
);

export default function Settings({ isOpen, onClose }) {
  const { currentUser, logout } = useAuth();
  const { currentTheme, setCurrentTheme, themes } = useTheme();
  const {
    notificationTime, 
    updateNotificationTime, 
    requestNotificationPermission,
    permissionStatus
  } = useNotifications();
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedName, setEditedName] = useState(currentUser?.displayName || '');
  const [selectedAvatar, setSelectedAvatar] = useState(0);

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await Promise.all([
        deleteUserGoals(currentUser.uid),
        deleteUserReflections(currentUser.uid),
        deleteUserDoc(currentUser.uid)
      ]);

      await currentUser.delete();
      
      setShowDeleteConfirmation(false);
      onClose();
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
    }
  };

  const handleNotificationPermission = async () => {
    const result = await requestNotificationPermission();
    switch (result) {
      case 'granted':
        // Show success message or update UI
        break;
      case 'denied':
        // Show message about enabling in browser settings
        break;
      case 'unsupported':
        // Show message about browser not supporting notifications
        break;
      default:
        // Handle default case (user closed prompt without choosing)
        break;
    }
  };

  const handleUpdateProfile = async () => {
    try {
      // Update display name
      await updateDoc(doc(db, 'users', currentUser.uid), {
        displayName: editedName,
        avatarSeed: selectedAvatar
      });

      // Update auth profile
      await updateProfile(currentUser, {
        displayName: editedName
      });

      setIsEditingProfile(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="flex items-end min-h-full">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="w-full h-[85vh] transform overflow-hidden bg-white rounded-t-2xl shadow-xl transition-all">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                  {/* Drag Handle */}
                  <div className="flex justify-center py-2">
                    <div className="w-12 h-1.5 rounded-full bg-gray-300" />
                  </div>
                  <div className="flex items-center justify-between px-4 pb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      Settings
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="px-4 py-6 space-y-6 overflow-y-auto" style={{ height: 'calc(85vh - 76px)' }}>
                  {/* Profile Section */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setIsEditingProfile(true)}
                  >
                    <img
                      src={`https://api.dicebear.com/7.x/adventurer-neutral/svg?seed=${currentUser?.avatarSeed || currentUser?.email}&mouth=variant01,variant27`}
                      alt="Profile"
                      className="w-16 h-16 rounded-full bg-white"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {currentUser?.displayName || 'User'}
                      </h4>
                      <p className="text-sm text-gray-500">{currentUser?.email}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>

                  {/* Theme Selection */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-900 px-4">Theme</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(themes || {}).map(([key, theme]) => (
                        <button
                          key={key}
                          onClick={() => setCurrentTheme(theme)}
                          className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
                            currentTheme?.name === theme.name
                              ? 'bg-primary-50 text-primary-900 ring-2 ring-primary-600'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-2xl">{theme.icon}</span>
                          <span className="text-sm font-medium">{theme.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Notification Settings */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900 px-4">Notifications</h4>
                    <div className="px-4">
                      {permissionStatus === 'granted' ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Daily Reminders</span>
                            <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                              Enabled
                            </span>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm text-gray-600">
                              Remind me daily at:
                            </label>
                            <input
                              type="time"
                              value={notificationTime}
                              onChange={(e) => updateNotificationTime(e.target.value)}
                              className="w-full px-4 py-2 rounded-lg border border-gray-200 
                                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                                text-gray-700 bg-white hover:border-primary-300 transition-colors
                                [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100
                                [&::-webkit-calendar-picker-indicator]:transition-opacity
                                [&::-webkit-calendar-picker-indicator]:cursor-pointer
                                [&::-webkit-calendar-picker-indicator]:hover:text-primary-500"
                              style={{
                                colorScheme: currentTheme?.name === 'Dark' ? 'dark' : 'light',
                                accentColor: currentTheme?.colors?.primary?.[500]
                              }}
                            />
                            <p className="text-xs text-gray-500">
                              You'll receive a daily reminder to update your goals and journal.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="bg-yellow-50 p-4 rounded-lg">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">
                                  Notifications are {permissionStatus === 'denied' ? 'blocked' : 'not enabled'}
                                </h3>
                                <div className="mt-2 text-sm text-yellow-700">
                                  <p>
                                    {permissionStatus === 'denied' 
                                      ? 'Please enable notifications in your browser settings to receive daily reminders.'
                                      : 'Enable notifications to receive daily reminders for your goals and journaling.'}
                                  </p>
                                </div>
                                {permissionStatus !== 'denied' && (
                                  <div className="mt-4">
                                    <button
                                      onClick={handleNotificationPermission}
                                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                    >
                                      Enable Notifications
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Settings Options */}
                  <div className="space-y-2">
                    {/* Only show notifications button if notifications aren't enabled */}
                    {permissionStatus !== 'granted' && (
                      <button
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 rounded-lg transition-colors"
                        onClick={handleNotificationPermission}
                      >
                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        Notifications
                      </button>
                    )}

                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setPrivacyModalOpen(true)}
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Privacy
                    </button>
                  </div>

                  {/* Danger Zone */}
                  <div className="space-y-2">
                    <button
                      onClick={() => setIsDangerZoneOpen(!isDangerZoneOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left text-sm hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span className="font-medium text-red-600">Danger Zone</span>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform ${isDangerZoneOpen ? 'rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Collapsible content */}
                    {isDangerZoneOpen && (
                      <div className="px-4 py-3 space-y-4">
                        <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                          <h4 className="text-sm font-medium text-red-800 mb-2">Delete Account</h4>
                          <p className="text-sm text-red-600 mb-3">
                            Once you delete your account, there is no going back. Please be certain.
                          </p>
                          <button
                            onClick={() => setShowDeleteConfirmation(true)}
                            className="text-sm font-medium text-red-600 hover:text-red-700"
                          >
                            Delete my account
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Logout Button */}
                  <button
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-auto"
                    onClick={handleLogout}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Logout
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Add Privacy Info Modal */}
      <PrivacyModal 
        isOpen={privacyModalOpen} 
        onClose={() => setPrivacyModalOpen(false)} 
      />

      {/* Add the DeleteAccountModal */}
      <DeleteAccountModal 
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteAccount}
      />

      {/* Add the ProfileEditModal */}
      <ProfileEditModal 
        isOpen={isEditingProfile} 
        onClose={() => setIsEditingProfile(false)}
        editedName={editedName}
        setEditedName={setEditedName}
        selectedAvatar={selectedAvatar}
        setSelectedAvatar={setSelectedAvatar}
        onSave={handleUpdateProfile}
      />
    </Transition>
  );
} 